import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BreadCrumb from "../Controls/BreadCrumb";
import CoachTodoList from "./CoachTodoList";
import API from "@src/API";
import "../../style/scss/Recruiter.scss";

function pick(obj, name) {
  if (!obj) return undefined;
  return obj[name] ?? obj[name.charAt(0).toLowerCase() + name.slice(1)];
}

export default function RecruiterDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await API.getAsync("/Recruiter/Dashboard");
      if (resp?.ok) {
        setData(resp.jsonData);
        setError("");
      } else {
        setError("Could not load your hiring portfolio.");
      }
    } catch {
      setError("Could not load your hiring portfolio.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const companies = pick(data, "Companies") || [];
  const activeJobs = pick(data, "ActiveJobCount") ?? 0;
  const matches = pick(data, "MatchCount") ?? 0;
  const inProcess = pick(data, "InProcessCount") ?? 0;

  return (
    <div className="pagebody recruiter-dashboard">
      <BreadCrumb props={[{ label: "Hiring", route: "/recruiter" }]} />
      <div className="tabbody">
        <div className="recruiter-dashboard__hero">
          <div>
            <h2>Hiring</h2>
            <p className="byline">
              CareerMaster matches candidates to your openings automatically — here is where each
              company stands.
            </p>
          </div>
          <div className="recruiter-dashboard__stats">
            <div>
              <span className="stat">{activeJobs}</span>
              <span className="statLabel">open roles</span>
            </div>
            <div>
              <span className="stat">{matches}</span>
              <span className="statLabel">matches</span>
            </div>
            <div>
              <span className="stat">{inProcess}</span>
              <span className="statLabel">in process</span>
            </div>
          </div>
        </div>

        {error ? <p className="recruiter-dashboard__error">{error}</p> : null}
        {loading && !data ? <p className="byline">Loading your companies…</p> : null}

        <CoachTodoList todos={pick(data, "Todos") || []} title="Where your attention pays off most" />

        <div className="tabsection">
          <div className="listTitle">
            <span>Your companies</span>
          </div>
          {companies.length === 0 ? (
            <p className="byline">
              You do not administer any companies yet. Ask a company administrator to add you, or{" "}
              <Link to="/employerEdit">create a company</Link>.
            </p>
          ) : (
            <ul className="recruiter-company-list">
              {companies.map((c) => {
                const id = pick(c, "EmployerID");
                const noMatches = pick(c, "JobsWithoutMatches") ?? 0;
                const calculating = pick(c, "JobsCalculating") ?? 0;
                const neverRun = pick(c, "JobsNeverRun") ?? 0;
                return (
                  <li key={id}>
                    <Link className="recruiter-company-list__name" to={`/company/${id}`}>
                      {pick(c, "EmployerName")}
                    </Link>
                    <div className="recruiter-company-list__counts">
                      <span>{pick(c, "ActiveJobCount") ?? 0} open</span>
                      <span>{pick(c, "MatchCount") ?? 0} matched</span>
                      <span>{pick(c, "InProcessCount") ?? 0} in process</span>
                    </div>
                    {calculating > 0 ? (
                      <div className="byline">{calculating} opening(s) are matching right now.</div>
                    ) : null}
                    {noMatches > 0 ? (
                      <div className="byline">
                        {noMatches} opening(s) have no candidates — usually pay or location.
                      </div>
                    ) : null}
                    {neverRun > 0 ? (
                      <div className="byline">{neverRun} opening(s) have never been matched.</div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
